<template>
  <ListingTemplate :customClass="'export-listing'">
    <template v-slot:body>
      <ListingHeader>
        <template v-slot:content>
          <v-col cols="12" class="pt-0 d-flex justify-flex-end export-filters">
            <v-text-field
              v-model.trim="listingSearch.search"
              clearable
              dense
              filled
              flat
              hide-details
              label="Search"
              solo
              :disabled="dataLoading"
              color="cyan"
              append-icon="mdi-magnify"
              class="export-search-field"
              @click:clear="clearSearch"
              @keydown.enter="filterRows"
            ></v-text-field>
            <v-menu
              v-model="datePicker"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  dense
                  filled
                  flat
                  hide-details
                  label="Date Range"
                  solo
                  readonly
                  clearable
                  prepend-inner-icon="mdi-calendar"
                  :value="formattedDate"
                  :disabled="dataLoading"
                  color="cyan"
                  class="ml-2 export-date-field"
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearFilter('dates')"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="dates"
                range
                color="cyan"
                :disabled="dataLoading"
                @change="applyDateFilter"
              ></v-date-picker>
            </v-menu>
            <v-menu
              max-height="400"
              max-width="250"
              left
              offset-y
              :close-on-content-click="false"
              content-class="white-background"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  depressed
                  class="ml-2 custom-bold-button btn-tab-hide"
                  color="cyan white--text"
                  :disabled="dataLoading"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon dark>mdi-table-edit</v-icon>
                </v-btn>
              </template>
              <draggable
                tag="ul"
                v-model="defaultColDefs"
                class="draggable-group"
                handle=".draggable-drag-icon"
                @change="updateVisible"
              >
                <li
                  v-for="column in configurableColumns"
                  :key="column.field"
                  class="draggable-group-item"
                >
                  <v-checkbox
                    v-model="defaultColShow"
                    dense
                    hide-details
                    class="mt-0 mb-0"
                    :value="column.field"
                    :label="column.headerName"
                    :disabled="Number(column.fixed) > 0 || dataLoading"
                    color="cyan"
                    @change="updateVisible"
                  ></v-checkbox>
                  <v-icon class="draggable-drag-icon" right color="cyan"
                    >mdi-drag</v-icon
                  >
                </li>
              </draggable>
            </v-menu>
          </v-col>
        </template>
      </ListingHeader>

      <ListingSearchCriteria
        v-if="searchEnabled"
        pageModule="export-listing"
        :basicSearchFields="['module type', 'email']"
        :allowedSearchFields="searchableArray"
      ></ListingSearchCriteria>

      <perfect-scrollbar
        :options="{ suppressScrollX: true }"
        class="scroll custom-box-top-inner-shadow"
        style="max-height: 90vh; position: relative"
      >
        <ListingTable
          :columnCount="columnLength() || 1"
          :dataLoading="dataLoading"
          :rowData="rowData"
        >
          <template v-slot:thead>
            <thead>
              <tr>
                <template v-for="cols in customThead">
                  <th
                    v-if="cols.visible && cols.field !== 'id'"
                    :key="cols.id || cols.field"
                    class="simple-table-th"
                    :class="{ 'text-center': cols.field === 'status' }"
                  >
                    <div
                      v-if="cols.sortable"
                      class="simple-table-text sortable"
                      @click="
                        !dataLoading &&
                          updateSorting({ field: cols.field, sort: cols.sort })
                      "
                    >
                      {{ cols.headerName }}
                    </div>
                    <div v-else class="simple-table-text">
                      {{ cols.headerName }}
                    </div>
                    <v-icon v-if="cols.sort === 'asc'" small color="cyan"
                      >mdi-sort-ascending</v-icon
                    >
                    <v-icon v-if="cols.sort === 'desc'" small color="cyan"
                      >mdi-sort-descending</v-icon
                    >
                  </th>
                </template>
              </tr>
            </thead>
          </template>
          <template v-slot:tbody>
            <tbody class="custom-border-bottom">
              <tr
                v-for="(data, index) in rowData"
                :key="index"
                class="service-listing-row"
              >
                <template v-for="cols in customThead">
                  <td
                    v-if="cols.visible && cols.field !== 'id'"
                    :key="cols.id || cols.field"
                    class="simple-table-td"
                  >
                    <template v-if="cols.field === 'module_name'">
                      <div class="mt-0 mb-1">
                        <v-chip
                          small
                          class="text-capitalize mt-1"
                          color="blue"
                          label
                          outlined
                        >
                          {{ data.module_name || data.module_type || "--" }}
                        </v-chip>
                      </div>
                    </template>
                    <template v-else-if="cols.field === 'status'">
                      <div
                        v-if="Number(data.status) > 0"
                        class="d-flex justify-center"
                      >
                        <v-chip
                          small
                          label
                          text-color="white"
                          :color="exportStatusColor(data.status)"
                        >
                          {{ exportStatusLabel(data.status) }}
                        </v-chip>
                      </div>
                      <div v-else class="d-flex justify-center">--</div>
                    </template>
                    <template v-else-if="cols.field === 'start_date'">
                      <div class="export-listing-value">
                        <p class="m-0 custom-nowrap-ellipsis">
                          {{
                            formatDate(data.start_date, cols.field) ||
                            "no start date"
                          }}
                        </p>
                      </div>
                    </template>
                    <template v-else-if="cols.field === 'end_date'">
                      <div class="export-listing-value">
                        <p class="m-0 custom-nowrap-ellipsis">
                          {{
                            formatDate(data.end_date, cols.field) ||
                            "no end date"
                          }}
                        </p>
                      </div>
                    </template>
                    <template v-else-if="cols.field === 'user_email'">
                      <div class="export-listing-value">
                        <p class="m-0 custom-nowrap-ellipsis">
                          {{ data.user_email || data.email || "no email" }}
                        </p>
                      </div>
                    </template>
                    <template v-else-if="cols.field === 'action'">
                      <v-tooltip v-if="data.file_url" top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            icon
                            small
                            color="green"
                            class="export-download-button"
                            :href="data.file_url"
                            download
                            target="_blank"
                            rel="noopener noreferrer"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon>mdi-microsoft-excel</v-icon>
                          </v-btn>
                        </template>
                        <span>Download file</span>
                      </v-tooltip>
                      <b v-else>no file available</b>
                    </template>
                    <template v-else-if="cols.field == 'added_at'">
                      <TableActivity :data="data">
                        <template v-slot:date_time>
                          {{ formatDateTime(data.added_at, cols.field) }}
                        </template>
                        <template v-slot:format_date_time>
                          {{ formatDateTime(data.added_at, cols.field) }}
                        </template>
                      </TableActivity>
                    </template>
                    <template v-else-if="cols.field == 'updated_at'">
                      <TableActivity
                        v-if="!lodash.isEmpty(data.updated_by)"
                        :data="data"
                      >
                        <template v-slot:display_name>
                          {{ data.updated_by.display_name }}
                        </template>
                        <template v-slot:date_time>
                          {{ formatDateTime(data.updated_at, cols.field) }}
                        </template>
                        <template v-slot:format_date_time>
                          {{ formatDateTime(data.updated_at, cols.field) }}
                        </template>
                      </TableActivity>
                    </template>
                  </td>
                </template>
              </tr>
              <tr v-if="lodash.isEmpty(rowData) && !dataLoading">
                <td :colspan="columnLength() || 1" class="py-4">
                  <p
                    class="m-0 row-not-found text-center font-weight-500 font-size-16"
                  >
                    No export history found.
                  </p>
                </td>
              </tr>
            </tbody>
          </template>
        </ListingTable>
      </perfect-scrollbar>

      <ListingFooter
        :dataLoading="dataLoading"
        :showingFrom="showingFrom"
        :showingTo="showingTo"
        :totalRows="totalRows"
        :currentPage="currentPage"
        :totalPages="totalPages"
      ></ListingFooter>
    </template>
  </ListingTemplate>
</template>

<script>
import draggable from "vuedraggable";
import CommonMixin from "@/core/plugins/common-mixin";
import ListingMixin from "@/core/plugins/listing-mixin";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import ListingTemplate from "@/view/pages/partials/Listing-Template.vue";
import ListingTable from "@/view/pages/partials/Listing-Table.vue";
import ListingHeader from "@/view/pages/partials/Listing-Header.vue";
import ListingFooter from "@/view/pages/partials/Listing-Footer.vue";
import ListingSearchCriteria from "@/view/pages/partials/Listing-Search-Criteria.vue";
import moment from "moment-timezone";
import TableActivity from "@/view/pages/partials/Table-Activity.vue";

moment.tz.setDefault(process.env.VUE_APP_TIMEZONE);

export default {
  name: "export-list",
  mixins: [CommonMixin, ListingMixin],
  components: {
    draggable,
    ListingTemplate,
    ListingTable,
    ListingHeader,
    ListingFooter,
    ListingSearchCriteria,
    TableActivity,
  },
  data() {
    return {
      pageModule: "export-listing",
      routeAPI: "export-list",
      routeName: "export",
      status: "all",
      customThead: [],
      datePicker: false,
    };
  },
  computed: {
    configurableColumns() {
      return this.defaultColDefs.filter(
        (column) =>
          column.field !== "id" &&
          (!column.checkbox || Number(column.checkbox) === 0)
      );
    },
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [{ title: "Export List" }]);
  },
  beforeMount() {
    this.lodash.assign(this.queryParams, this.$route.query);

    this.status = this.$route.query.status || this.status;

    this.paginationPageSize =
      window.localStorage.getItem(this.pageModule) || 50;
  },
  methods: {
    exportStatusLabel(status) {
      if (status == 1) {
        return "Pending";
      }
      if (status == 2) {
        return "Completed";
      }
      if (status == 3) {
        return "Failed";
      }
      return "--";
    },
    exportStatusColor(status) {
      if (status == 1) {
        return "orange";
      }
      if (status == 2) {
        return "green";
      }
      if (status == 3) {
        return "red";
      }
      return "grey";
    },
    applyDateFilter() {
      this.currentPage = 1;
      this.getRows();
    },
    clearFilter(field) {
      if (field === "dates") {
        this.dates = [];
        this.currentPage = 1;
        this.getRows();
      }
    },
  },
};
</script>

<style scoped>
.export-filters {
  gap: 8px;
}

.export-search-field {
  flex: 0 1 320px;
}

.export-date-field {
  flex: 0 1 260px;
}

.export-download-button {
  margin: 0;
}
</style>
